import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApiService } from '../services/authApi';
import { AUTH_ERROR_MESSAGES, AUTH_ROUTES } from '../constants';
import AuthHero from '../components/AuthHero';

const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // State management
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(true);
    const [isResetComplete, setIsResetComplete] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<{
        newPassword?: string;
        confirmPassword?: string;
    }>({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Extract token from URL - Handle both search params and hash fragments (Supabase way)
    const getTokenFromURL = () => {
        // First try search params
        const searchToken = searchParams.get('access_token') || searchParams.get('token');
        if (searchToken) {
            return searchToken;
        }

        // Then try hash fragments (Supabase method)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hashToken = hashParams.get('access_token') || hashParams.get('token');
        if (hashToken) {
            return hashToken;
        }

        return '';
    };

    const token = getTokenFromURL();

    // Verify token on component mount
    useEffect(() => {
        const verifyToken = async () => {
            // Skip verification if reset is already complete (we're navigating away)
            if (isResetComplete) {
                return;
            }


            if (!token) {
                navigate(AUTH_ROUTES.FORGOT_PASSWORD, {
                    state: { error: 'Invalid reset link. Please request a new password reset.' }
                });
                return;
            }

            try {
                const result = await authApiService.verifyResetToken(token);

                if (result.valid) {
                    setIsVerifying(false);
                } else {
                    navigate(AUTH_ROUTES.FORGOT_PASSWORD, {
                        state: { error: result.error || 'This reset link is invalid or has expired.' }
                    });
                }
            } catch (err: any) {
                console.error('❌ DEBUG: Token verification failed:', err);
                navigate(AUTH_ROUTES.FORGOT_PASSWORD, {
                    state: { error: 'Failed to verify reset link. Please try again.' }
                });
            }
        };

        verifyToken();
    }, [token, navigate, isResetComplete]);

    // Form validation
    const validateForm = () => {
        const errors: { newPassword?: string; confirmPassword?: string } = {};

        // Password validation
        if (!newPassword) {
            errors.newPassword = 'New password is required';
        } else if (newPassword.length < 8) {
            errors.newPassword = 'Password must be at least 8 characters long';
        } else if (newPassword.length > 128) {
            errors.newPassword = 'Password is too long (maximum 128 characters)';
        } else {
            // Password complexity check
            const hasUpperCase = /[A-Z]/.test(newPassword);
            const hasLowerCase = /[a-z]/.test(newPassword);
            const hasNumbers = /\d/.test(newPassword);

            if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
                errors.newPassword = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
            }
        }

        // Confirm password validation
        if (!confirmPassword) {
            errors.confirmPassword = AUTH_ERROR_MESSAGES.CONFIRM_PASSWORD_REQUIRED;
        } else if (newPassword !== confirmPassword) {
            errors.confirmPassword = AUTH_ERROR_MESSAGES.PASSWORDS_DONT_MATCH;
        }

        return errors;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Clear previous errors
        setError('');
        setFieldErrors({});

        // Validate form
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        setIsLoading(true);

        try {
            const response = await authApiService.resetPassword(token, newPassword);

            // Mark reset as complete BEFORE navigating to prevent re-verification
            setIsResetComplete(true);

            // Redirect to login with success message
            navigate(AUTH_ROUTES.LOGIN, {
                state: {
                    message: 'Your password has been reset successfully! You can now log in with your new password.'
                }
            });
        } catch (err: any) {
            console.error('❌ DEBUG: Password reset failed:', err);
            setError(err.message || 'Failed to reset password. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (field: 'newPassword' | 'confirmPassword', value: string) => {
        if (field === 'newPassword') {
            setNewPassword(value);
        } else {
            setConfirmPassword(value);
        }

        // Clear field error when user starts typing
        if (fieldErrors[field]) {
            setFieldErrors(prev => ({
                ...prev,
                [field]: undefined,
            }));
        }

        // Clear general error
        if (error) {
            setError('');
        }
    };

    // Show loading while verifying token
    if (isVerifying) {
        return (
            <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Verifying reset link...</p>
                    <p className="text-sm text-gray-500 mt-2">Please wait while we validate your reset token</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen flex items-start justify-center relative overflow-hidden pt-16"
            style={{
                background: '#015FA6',
                fontFamily: 'Manrope, sans-serif'
            }}
        >
            {/* Background decorative ellipse */}
            <div
                style={{
                    position: 'absolute',
                    width: '1078.81px',
                    height: '1064.85px',
                    left: '-514px',
                    bottom: '-209.85px',
                    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(116, 153, 191, 0.465385) 41.83%, rgba(237, 242, 247, 0) 100%)',
                    transform: 'rotate(-153.16deg)',
                    borderRadius: '50%',
                    pointerEvents: 'none'
                }}
            />

            {/* Main content container */}
            <div className="w-full max-w-[480px] px-4 py-6 relative z-10">
                {/* Logo */}
                <div className="text-center mb-6">
                    <img
                        src="/branding/logo_blue.svg"
                        alt="Hush"
                        className="mx-auto cursor-pointer"
                        style={{
                            width: 'auto',
                            height: '50px',
                            objectFit: 'contain'
                        }}
                        onClick={() => navigate('/')}
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const parent = e.currentTarget.parentElement;
                            if (parent) {
                                parent.innerHTML = '<div style="font-size: 32px; font-weight: 700; color: white; font-family: Manrope;">Hush</div>';
                            }
                        }}
                    />
                </div>

                {/* Header */}
                <div className="text-center mb-8">
                    <h1
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 700,
                            fontSize: '32px',
                            lineHeight: '100%',
                            color: '#FFFFFF',
                            marginBottom: '12px'
                        }}
                        className="text-2xl sm:text-3xl"
                    >
                        Reset password
                    </h1>
                    <p
                        style={{
                            fontFamily: 'Manrope',
                            fontWeight: 300,
                            fontSize: '14px',
                            lineHeight: '150%',
                            textAlign: 'center',
                            color: '#FFFFFF'
                        }}
                        className="text-sm"
                    >
                        Must be at least 8 characters long
                    </p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                    {/* New Password Field */}
                    <div>
                        <label
                            htmlFor="newPassword"
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '18px',
                                letterSpacing: '-0.154px',
                                color: '#FFFFFF',
                                display: 'block',
                                marginBottom: '6px'
                            }}
                        >
                            New password
                        </label>
                        <div className="relative">
                            <input
                                id="newPassword"
                                name="newPassword"
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                required
                                value={newPassword}
                                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                                placeholder="@#*%"
                                disabled={isLoading}
                                style={{
                                    width: '100%',
                                    height: '45px',
                                    padding: '0 50px 0 14px',
                                    fontFamily: 'Manrope',
                                    fontSize: '14px',
                                    lineHeight: '18px',
                                    letterSpacing: '-0.154px',
                                    color: 'rgba(1, 95, 166, 0.5)',
                                    background: '#F7FAFC',
                                    border: fieldErrors.newPassword ? '1px solid #EF4444' : '1px solid #CBD5E0',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    borderRadius: '10px',
                                    outline: 'none'
                                }}
                                className="w-full"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={isLoading}
                                style={{
                                    position: 'absolute',
                                    right: '14px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    padding: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    {showPassword ? (
                                        <path
                                            d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22"
                                            stroke="rgba(1, 95, 166, 0.5)"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    ) : (
                                        <>
                                            <path
                                                d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                                                stroke="rgba(1, 95, 166, 0.5)"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="3"
                                                stroke="rgba(1, 95, 166, 0.5)"
                                                strokeWidth="2"
                                            />
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                        {fieldErrors.newPassword && (
                            <p className="mt-2 text-sm text-red-300 flex items-center gap-2">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                {fieldErrors.newPassword}
                            </p>
                        )}
                    </div>

                    {/* Confirm Password Field */}
                    <div>
                        <label
                            htmlFor="confirmPassword"
                            style={{
                                fontFamily: 'Manrope',
                                fontWeight: 500,
                                fontSize: '14px',
                                lineHeight: '18px',
                                letterSpacing: '-0.154px',
                                color: '#FFFFFF',
                                display: 'block',
                                marginBottom: '6px'
                            }}
                        >
                            Confirm new password
                        </label>
                        <div className="relative">
                            <input
                                id="confirmPassword"
                                name="confirmPassword"
                                type={showConfirmPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                required
                                value={confirmPassword}
                                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                placeholder="@#*%"
                                disabled={isLoading}
                                style={{
                                    width: '100%',
                                    height: '45px',
                                    padding: '0 50px 0 14px',
                                    fontFamily: 'Manrope',
                                    fontSize: '14px',
                                    lineHeight: '18px',
                                    letterSpacing: '-0.154px',
                                    color: 'rgba(1, 95, 166, 0.5)',
                                    background: '#F7FAFC',
                                    border: fieldErrors.confirmPassword ? '1px solid #EF4444' : '1px solid #CBD5E0',
                                    boxShadow: 'inset 0px 2px 0px rgba(231, 235, 238, 0.2)',
                                    borderRadius: '10px',
                                    outline: 'none'
                                }}
                                className="w-full"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                disabled={isLoading}
                                style={{
                                    position: 'absolute',
                                    right: '14px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    padding: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    {showConfirmPassword ? (
                                        <path
                                            d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22"
                                            stroke="rgba(1, 95, 166, 0.5)"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    ) : (
                                        <>
                                            <path
                                                d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                                                stroke="rgba(1, 95, 166, 0.5)"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="3"
                                                stroke="rgba(1, 95, 166, 0.5)"
                                                strokeWidth="2"
                                            />
                                        </>
                                    )}
                                </svg>
                            </button>
                        </div>
                        {fieldErrors.confirmPassword && (
                            <p className="mt-2 text-sm text-red-300 flex items-center gap-2">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path
                                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                {fieldErrors.confirmPassword}
                            </p>
                        )}
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isLoading || !newPassword.trim() || !confirmPassword.trim()}
                        style={{
                            width: '100%',
                            height: '45px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            fontFamily: 'Manrope',
                            fontWeight: 600,
                            fontSize: '16px',
                            lineHeight: '22px',
                            color: '#015FA6',
                            background: '#F8F9FB',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: (isLoading || !newPassword.trim() || !confirmPassword.trim()) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            marginTop: '16px',
                            opacity: (!newPassword.trim() || !confirmPassword.trim()) ? 0.6 : 1
                        }}
                        className="hover:shadow-lg hover:bg-white"
                    >
                        {isLoading ? 'Resetting password...' : 'Reset password'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;
